/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
    Button,
    Card,
    Form,
    Icon,
    Input,
    Modal,
    Popover,
    Row,
    Select,
    Table,
    Tag,
    message,
} from 'antd';
import { services } from '@comall-backend-builder/core';
import { errorHandle } from '../../services';
import './index.less';
import {
    RULE_DIMENSION,
    RULE_STATUS,
    RULE_DIMENSION_OPTIONS,
    RULE_STATUS_OPTIONS,
} from './constants';

const api = services.api;
const { Option } = Select;
const { confirm } = Modal;

const LIST_API_PATH = '/products/rich-text-rules/findList';
const UPDATE_STATUS_API_PATH = '/products/rich-text-rules/updateStatus';
const DELETE_API_PATH = '/products/rich-text-rules/delete';

export { RULE_DIMENSION, RULE_STATUS } from './constants';

const getDimensionLabel = (dimension?: number) => {
    const found = RULE_DIMENSION_OPTIONS.find((item) => item.value === dimension);
    return found ? found.label : '-';
};

const stripHtml = (html?: string) => {
    if (!html) {
        return '';
    }
    return html
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

/** SPU：N 个 SPU；品类+大类：{首项品类名} 等 N 项 */
const getScopeSummary = (record: any) => {
    if (record.ruleDimension === RULE_DIMENSION.SPU) {
        const count = (record.spuList || []).length;
        return count ? `${count} 个 SPU` : '-';
    }
    if (record.ruleDimension === RULE_DIMENSION.CATEGORY) {
        const list = record.categoryList || [];
        const count = list.length;
        if (!count) {
            return '-';
        }
        const first = list[0];
        const firstName =
            first.categoryName ||
            first.category_name ||
            first.bigCategoryName ||
            first.big_category_name ||
            '';
        return `${firstName} 等 ${count} 项`;
    }
    return '-';
};

/** Popover 明细文案：品类 - 大类 */
const getScopeTexts = (record: any): string[] => {
    if (record.ruleDimension === RULE_DIMENSION.SPU) {
        return (record.spuList || []).map((item: any) => item.spuCode).filter(Boolean);
    }
    if (record.ruleDimension === RULE_DIMENSION.CATEGORY) {
        return (record.categoryList || []).map((item: any) => {
            const category = item.categoryName || item.category_name || '';
            const big = item.bigCategoryName || item.big_category_name || '';
            if (category && big) {
                return `${category} - ${big}`;
            }
            return category || big || '-';
        });
    }
    return [];
};

const renderScopePopoverContent = (texts: string[]) => {
    if (!texts.length) {
        return <div className="batch-detail-config-list__scope-empty">暂无范围数据</div>;
    }
    return (
        <div className="batch-detail-config-list__scope-popover">
            {texts.map((text, index) => (
                <div key={`${text}-${index}`} className="batch-detail-config-list__scope-item">
                    {text}
                </div>
            ))}
        </div>
    );
};

const cleanParams = (params: Record<string, any>) => {
    const next = { ...params };
    Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === null || next[key] === '') {
            delete next[key];
        }
    });
    return next;
};

type CategoryOption = {
    code: string;
    optionValue: string;
};

const BatchDetailConfigListCom: React.FC<any> = ({ form }) => {
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState<any[]>([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchParams, setSearchParams] = useState<Record<string, any>>({});
    const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);

    const fetchList = (query: Record<string, any>) => {
        const payload = cleanParams(query);
        setLoading(true);
        api.post(payload, { apiPath: LIST_API_PATH })
            .then((res: any) => {
                const list = res?.result || [];
                setTableData(list);
                setPageNum(res?.page || payload.page || 1);
                setTotal(res?.total_num || res?.totalNum || 0);
                setPageSize(payload.perPage || pageSize);
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    const getList = (params: Record<string, any> = {}) => {
        fetchList({
            page: 1,
            perPage: pageSize,
            ...searchParams,
            ...params,
        });
    };

    /** 商品品类码表 A005（属性 ZHPPL），选项展示为序号 + 名称 */
    const loadCategoryOptions = () => {
        api.get({ attr_code: 'ZHPPL' }, { apiPath: '/attributes/optionsList' })
            .then((res: any) => {
                const list = Array.isArray(res) ? res : res?.result || res?.data || [];
                setCategoryOptions(
                    list.map((item: any) => ({
                        code: String(item.code ?? item.id ?? ''),
                        optionValue: item.optionValue || item.name || '',
                    }))
                );
            })
            .catch(errorHandle);
    };

    useEffect(() => {
        loadCategoryOptions();
        getList({ page: 1, perPage: pageSize });
    }, []);

    const handleSearch = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFields((_err: any, values: any) => {
            const params = cleanParams({
                ruleDimension: values.ruleDimension,
                ruleStatus: values.ruleStatus,
                ruleName: values.ruleName ? values.ruleName.trim() : undefined,
                spuCode: values.spuCode ? values.spuCode.trim() : undefined,
                categoryCode: values.categoryCode,
            });
            setSearchParams(params);
            fetchList({ ...params, page: 1, perPage: pageSize });
        });
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({});
        fetchList({ page: 1, perPage: pageSize });
    };

    const handleAdd = () => {
        services.navigation.goto('/product/batchDetailConfig/add');
    };

    const handleEdit = (record: any) => {
        services.navigation.goto(`/product/batchDetailConfig/edit/${record.ruleId}`);
    };

    const handleToggleStatus = (record: any) => {
        const isEnable = record.ruleStatus !== RULE_STATUS.ENABLED;
        const actionText = isEnable ? '启用' : '停用';
        const nextStatus = isEnable ? RULE_STATUS.ENABLED : RULE_STATUS.DISABLED;
        const ruleId = record.ruleId || '';
        const ruleName = record.ruleName || '';

        confirm({
            title: `${actionText}确认`,
            content: `确认${actionText}规则「${ruleId} ${ruleName}」？`,
            okText: '确定',
            cancelText: '取消',
            onOk() {
                return api
                    .post(
                        { ruleId: record.ruleId, ruleStatus: nextStatus },
                        { apiPath: UPDATE_STATUS_API_PATH }
                    )
                    .then(() => {
                        message.success(isEnable ? '已启用' : '已停用');
                        getList({ page: pageNum, perPage: pageSize });
                    })
                    .catch(errorHandle);
            },
        });
    };

    const handleDelete = (record: any) => {
        if (record.ruleStatus === RULE_STATUS.ENABLED) {
            message.warning('启用中的规则需先停用后再删除');
            return;
        }

        confirm({
            title: '删除确认',
            content: '删除后不可恢复，是否确认删除？',
            okText: '确定',
            cancelText: '取消',
            onOk() {
                return api
                    .post({}, { apiPath: `${DELETE_API_PATH}?ruleId=${record.ruleId}` })
                    .then(() => {
                        message.success('删除成功');
                        const nextPage =
                            tableData.length === 1 && pageNum > 1 ? pageNum - 1 : pageNum;
                        getList({ page: nextPage, perPage: pageSize });
                    })
                    .catch(errorHandle);
            },
        });
    };

    const columns: any[] = [
        {
            title: '规则ID',
            dataIndex: 'ruleId',
            key: 'ruleId',
            width: 140,
        },
        {
            title: '规则名称',
            dataIndex: 'ruleName',
            key: 'ruleName',
            width: 160,
            ellipsis: true,
        },
        {
            title: '规则维度',
            dataIndex: 'ruleDimension',
            key: 'ruleDimension',
            width: 120,
            render: (value: number) => {
                const label = getDimensionLabel(value);
                return <Tag color="blue">{label}</Tag>;
            },
        },
        {
            title: '范围',
            key: 'scopeSummary',
            width: 180,
            render: (_: any, record: any) => {
                const summary = getScopeSummary(record);
                if (summary === '-') {
                    return '-';
                }
                const texts = getScopeTexts(record);
                return (
                    <Popover
                        content={renderScopePopoverContent(texts)}
                        trigger="hover"
                        placement="bottomLeft"
                        overlayClassName="batch-detail-config-list__scope-overlay"
                    >
                        <a>{summary}</a>
                    </Popover>
                );
            },
        },
        {
            title: '富文本摘要',
            dataIndex: 'richText',
            key: 'richText',
            ellipsis: true,
            render: (text: string) => {
                const summary = stripHtml(text).slice(0, 50) || '-';
                return <span title={summary}>{summary}</span>;
            },
        },
        {
            title: '状态',
            dataIndex: 'ruleStatus',
            key: 'ruleStatus',
            width: 90,
            render: (status: number) =>
                status === RULE_STATUS.ENABLED ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>,
        },
        {
            title: '操作人',
            key: 'operator',
            width: 160,
            render: (_: any, record: any) =>
                record.operatorUserId
                    ? `${record.operatorUserId}-${record.operatorName || ''}`
                    : record.operatorName || '-',
        },
        {
            title: '更新时间',
            dataIndex: 'updateTime',
            key: 'updateTime',
            width: 170,
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            fixed: 'right' as const,
            render: (_: any, record: any) => {
                const enabled = record.ruleStatus === RULE_STATUS.ENABLED;
                return (
                    <span className="batch-detail-config-list__actions">
                        <a onClick={() => handleEdit(record)}>编辑</a>
                        <a onClick={() => handleToggleStatus(record)}>
                            {enabled ? '停用' : '启用'}
                        </a>
                        {!enabled && (
                            <a className="is-danger" onClick={() => handleDelete(record)}>
                                删除
                            </a>
                        )}
                    </span>
                );
            },
        },
    ];

    return (
        <div className="batch-detail-config-list">
            <Card>
                <Form
                    layout="inline"
                    onSubmit={handleSearch}
                    className="batch-detail-config-list__filter"
                >
                    <Form.Item label="规则维度">
                        {form.getFieldDecorator('ruleDimension')(
                            <Select allowClear placeholder="请选择" style={{ width: 160 }}>
                                {RULE_DIMENSION_OPTIONS.map((item) => (
                                    <Option key={item.value} value={item.value}>
                                        {item.label}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                    <Form.Item label="状态">
                        {form.getFieldDecorator('ruleStatus')(
                            <Select allowClear placeholder="请选择" style={{ width: 120 }}>
                                {RULE_STATUS_OPTIONS.map((item) => (
                                    <Option key={item.value} value={item.value}>
                                        {item.label}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                    <Form.Item label="SPU">
                        {form.getFieldDecorator('spuCode')(
                            <Input allowClear placeholder="请输入SPU编码" style={{ width: 180 }} />
                        )}
                    </Form.Item>
                    <Form.Item label="商品品类">
                        {form.getFieldDecorator('categoryCode')(
                            <Select
                                allowClear
                                showSearch
                                placeholder="请选择"
                                style={{ width: 220 }}
                                filterOption={(input, option) => {
                                    const text = (option.props.children || '').toString();
                                    return (
                                        text.toLowerCase().indexOf(input.trim().toLowerCase()) >= 0
                                    );
                                }}
                            >
                                {categoryOptions.map((item) => (
                                    <Option key={item.code} value={item.code}>
                                        {item.code} {item.optionValue}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                    <Form.Item label="规则名称">
                        {form.getFieldDecorator('ruleName')(
                            <Input allowClear placeholder="请输入规则名称" style={{ width: 200 }} />
                        )}
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" icon="search">
                            搜索
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                            重置
                        </Button>
                    </Form.Item>
                </Form>

                <Row style={{ marginBottom: 16 }}>
                    <Button type="primary" onClick={handleAdd}>
                        <Icon type="plus" />
                        新建规则
                    </Button>
                </Row>
                <Table
                    rowKey={(record) => record.ruleId}
                    columns={columns}
                    dataSource={tableData}
                    loading={loading}
                    scroll={{ x: 1200 }}
                    locale={{ emptyText: '暂无移动端详情批量配置' }}
                    pagination={{
                        total,
                        current: pageNum,
                        pageSize,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (count) => `共 ${count} 条记录`,
                        onChange: (page, size) => {
                            getList({ page, perPage: size || pageSize });
                        },
                        onShowSizeChange: (_current, size) => {
                            getList({ page: 1, perPage: size });
                        },
                    }}
                />
            </Card>
        </div>
    );
};

export const BatchDetailConfigListView = Form.create()(BatchDetailConfigListCom);
export default BatchDetailConfigListView;
